import { RETURN_PROCESSING_FLOW_RULES } from "@redotech/redo-model/return-flow/product-processing-spec";
import { ReturnProcessingDecisionState } from "@redotech/redo-return-ui/flows/return-flow/return-processing-decision";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

const DECISION_KEY_LABELS: Record<string, string> = {
  item_like_new: "Item like new",
  factory_sealed: "Factory sealed",
  new_condition: "New condition",
  item_wear: "Item wear",
  new_condition_after_cleaning: "New condition after cleaning",
};

export const ReturnProcessingDecisionDetails = memo(
  function ReturnProcessingDecisionDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<ReturnProcessingDecisionState>) {
    const decisionRuleOptions = useMemo(
      () =>
        RETURN_PROCESSING_FLOW_RULES.map((rule) => ({
          type: "text" as const,
          id: rule,
          text: DECISION_KEY_LABELS[rule] ?? rule,
          value: rule,
        })),
      [],
    );
    const selectedDecisionRule = useMemo(() => {
      if (!state.decisionKey) {
        return undefined;
      }
      return decisionRuleOptions.find(
        (option) => option.id === state.decisionKey,
      );
    }, [decisionRuleOptions, state.decisionKey]);
    return (
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Prompt
          </Text>
          <RedoTextareaInputField
            placeholder="Question shown to the warehouse worker"
            setValue={(prompt) => setState((state) => ({ ...state, prompt }))}
            value={state.prompt}
          />
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Decision rule
          </Text>
          <RedoSingleSelectDropdownInput
            options={decisionRuleOptions}
            optionSelected={({ item }) =>
              setState((state) => ({ ...state, decisionKey: item.value }))
            }
            placeholder="Select a rule…"
            selectedItem={selectedDecisionRule}
            size="xs"
          />
          <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
            Determines which built-in evaluator records the answer.
          </Text>
        </Flex>
        <Flex gap="sm">
          <Flex dir="column" gap="xs" style={{ flex: 1 }}>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              True label
            </Text>
            <RedoTextInput
              placeholder="Yes"
              setValue={(trueLabel) =>
                setState((state) => ({ ...state, trueLabel }))
              }
              value={state.trueLabel}
            />
          </Flex>
          <Flex dir="column" gap="xs" style={{ flex: 1 }}>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              False label
            </Text>
            <RedoTextInput
              placeholder="No"
              setValue={(falseLabel) =>
                setState((state) => ({ ...state, falseLabel }))
              }
              value={state.falseLabel}
            />
          </Flex>
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            On true →
          </Text>
          {stepSelect({
            value: state.nextTrue,
            valueChange: (nextTrue) =>
              setState((state) => ({ ...state, nextTrue })),
            handleIndex: 0,
          })}
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            On false →
          </Text>
          {stepSelect({
            value: state.nextFalse,
            valueChange: (nextFalse) =>
              setState((state) => ({ ...state, nextFalse })),
            handleIndex: 1,
          })}
        </Flex>
      </Flex>
    );
  },
);
